// What the FIELD says about one activity, by category.
//
// Owner's rule (2026-09-17): issues track BIM coordination, construction
// tracking tracks the field, checklists track management. So a field
// activity's completion is COUNTED from the model elements in its scope -
// cable tray, lighting fixtures, conduit, duct, pipe - and never read off
// a status somebody typed. Each category carries its own percentage, the
// way an activity carries the progress its issues report.
//
// Two numbers per row, because they answer different questions:
//   installed  how much is finished
//   progress   weighted, so work under way counts for part of it
//              (procured 25, being installed 60, installed 100)
import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Modal, Progress, Segmented, Skeleton, Space, Table, Tag, Typography, message } from "antd";
import { url, tokenRef } from "api";

const STATUS_LABEL = {
  projected: "Projected",
  procured: "Procured",
  being_installed: "Being installed",
  installed: "Installed",
};

export async function fetchActivityCategories(projectId, activityId) {
  const r = await fetch(`${url}/projects/${projectId}/field-tracking/activity/${activityId}/categories`, {
    headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
  return body;
}

async function api(path, options = {}) {
  const r = await fetch(`${url}${path}`, {
    ...options,
    headers: { Authorization: `Bearer ${localStorage.getItem(tokenRef)}`, "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
  return body;
}

// Nothing is tracked yet: propose which model elements this install is,
// from the words the schedule already carries, and let the owner tick.
// Counted, never applied on its own - the proposal is not the decision.
function LinkProposal({ projectId, activityId, onLinked }) {
  const [state, setState] = useState({ loading: true });
  const [picked, setPicked] = useState([]);
  const [onLevelOnly, setOnLevelOnly] = useState(true);
  // The model has no system field: a busduct is filed under Cable Trays
  // and typed "Electrical Busway". When some type names say the system
  // this activity does, narrowing to them is the honest default.
  const [typeOnly, setTypeOnly] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    api(`/projects/${projectId}/field-tracking/activity/${activityId}/element-proposal`)
      .then((d) => { if (!alive) return; setState({ loading: false, data: d }); setPicked((d.proposals || []).map((p) => p.category)); })
      .catch((e) => { if (alive) setState({ loading: false, err: e.message }); });
    return () => { alive = false; };
  }, [projectId, activityId]);

  if (state.loading) return <Skeleton active paragraph={{ rows: 2 }} />;
  if (state.err) return <Alert type="error" showIcon message={state.err} />;
  const d = state.data || {};
  if (!d.proposals || !d.proposals.length) {
    return <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>{d.note || d.reason || "Pick this activity's elements on the construction tracking board."}</Typography.Paragraph>;
  }

  const link = async () => {
    setSaving(true);
    try {
      const r = await api(`/projects/${projectId}/field-tracking/activity/${activityId}/link`, {
        method: "POST",
        body: JSON.stringify({
          categories: picked,
          level: onLevelOnly && d.level ? d.level : "",
          typeOnly: typeOnly && d.typeNarrowing,
        }),
      });
      message.success(`Linked ${r.linked} model element${r.linked === 1 ? "" : "s"} to this activity`);
      onLinked();
    } catch (e) { message.error(e.message); } finally { setSaving(false); }
  };

  return (
    <div style={{ marginTop: 12, borderTop: "1px solid #eee", paddingTop: 12 }}>
      <Typography.Paragraph style={{ marginBottom: 6 }}>
        <b>Which elements is this install?</b>{" "}
        <span style={{ color: "#888" }}>
          {d.matchedBy === "trade"
            ? "Guessed from the package's trade letters - check it."
            : "Read from the activity's own words."}
        </span>
      </Typography.Paragraph>
      <Checkbox.Group value={picked} onChange={setPicked} style={{ display: "block" }}>
        {d.proposals.map((p) => (
          <div key={p.category} style={{ padding: "2px 0" }}>
            <Checkbox value={p.category}>
              {p.category}{" "}
              <span style={{ color: "#888" }}>
                {p.total} in the model{d.level ? ` · ${p.onLevel} on ${d.level}` : ""}
                {p.onType ? ` · ${p.onType} typed ${p.typeSamples && p.typeSamples[0] ? `"${p.typeSamples[0]}"` : "for this system"}` : ""}
              </span>
            </Checkbox>
          </div>
        ))}
      </Checkbox.Group>
      <Space style={{ marginTop: 10 }} wrap>
        {d.level ? (
          <Checkbox checked={onLevelOnly} onChange={(e) => setOnLevelOnly(e.target.checked)}>
            Only elements on {d.level}
          </Checkbox>
        ) : null}
        {d.typeNarrowing ? (
          <Checkbox checked={typeOnly} onChange={(e) => setTypeOnly(e.target.checked)}>
            Only types that name this system
          </Checkbox>
        ) : null}
        <Button type="primary" size="small" loading={saving} disabled={!picked.length} onClick={link}>
          Link these elements
        </Button>
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
        {d.note} Linking can be redone at any time - it replaces this pick rather than adding a second one.
      </Typography.Paragraph>
    </div>
  );
}

export default function FieldProgressByCategory({ projectId, activity, open, onClose, canLink = false }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  // The field reports in TRACKED categories (busway, cable tray, single
  // line, branch, conduits, racks - owner, 2026-09-19); the model's own
  // categories stay one click away.
  const [view, setView] = useState("tracked");
  const activityId = activity && (activity._id || activity.id);

  useEffect(() => {
    if (!open || !activityId) return;
    let alive = true;
    setLoading(true); setErr(""); setData(null);
    fetchActivityCategories(projectId, activityId)
      .then((d) => { if (alive) setData(d); })
      .catch((e) => { if (alive) setErr(e.message || "Could not read the field progress"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, projectId, activityId, reload]);

  const columns = [
    { title: "Category", dataIndex: "category", render: (v) => <b>{v}</b> },
    { title: "Elements", dataIndex: "total", width: 90, align: "right" },
    {
      title: "Installed",
      dataIndex: "percentInstalled",
      width: 190,
      render: (v, r) => (
        <span title={`${r.byStatus.installed} of ${r.total} installed`}>
          <Progress percent={v} size="small" status={v >= 100 ? "success" : "active"} />
        </span>
      ),
    },
    {
      title: "Progress",
      dataIndex: "percentWeighted",
      width: 110,
      align: "right",
      render: (v, r) => (
        <span title={`Weighted: ${Object.entries(r.byStatus).filter(([, n]) => n).map(([k, n]) => `${n} ${STATUS_LABEL[k] || k}`).join(", ")}`}>
          {v}%
        </span>
      ),
    },
  ];

  const empty = data && !data.total;
  const buckets = (data && data.buckets) || [];
  const showBuckets = buckets.length > 0 && view === "tracked";
  const n0 = (v) => Math.round(Number(v) || 0).toLocaleString();
  const bucketColumns = [
    { title: "Tracked category", dataIndex: "label", render: (v, r) => <b>{v}{r.bucket === "conduits" ? <span style={{ color: "#888", fontWeight: 400 }}> (single line + branch + fittings)</span> : null}</b> },
    { title: "Elements", dataIndex: "elements", width: 90, align: "right", render: (v) => n0(v) },
    {
      title: "Installed",
      dataIndex: "percentInstalled",
      width: 190,
      render: (v) => <Progress percent={v} size="small" status={v >= 100 ? "success" : "active"} />,
    },
    {
      title: "Installed / remaining",
      key: "amounts",
      width: 200,
      render: (_, r) => (r.measure === "count"
        ? <span>{n0(r.byStatus.installed.n)} of {n0(r.elements)}</span>
        : <span>{n0(r.installedFt)} ft / <b>{n0(r.remainingFt)} ft</b></span>),
    },
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      title={`Field progress - ${(activity && activity.name) || "activity"}`}
    >
      {loading ? <Skeleton active /> : err ? <Alert type="error" message={err} showIcon /> : empty ? (
        <Alert
          type="info"
          showIcon
          message="No model elements are tracked for this activity yet"
          description={
            <>
              Until its elements are linked the field has said nothing about it, and a typed percentage is not the same claim.
              {canLink && activityId ? <LinkProposal projectId={projectId} activityId={activityId} onLinked={() => setReload((n) => n + 1)} /> : null}
            </>
          }
        />
      ) : data ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
            Counted from {data.total} tracked element{data.total === 1 ? "" : "s"} in this activity's scope
            {data.lists && data.lists.length ? ` (${data.lists.map((l) => l.name).join(", ")})` : ""}.
            {" "}
            <b>{data.percentInstalled}% installed</b>, {data.percentWeighted}% including work under way.
          </Typography.Paragraph>
          <div style={{ marginBottom: 12 }}>
            {Object.entries(data.byStatus || {}).filter(([, n]) => n).map(([k, n]) => (
              <Tag key={k} color={k === "installed" ? "green" : k === "being_installed" ? "blue" : k === "procured" ? "gold" : "default"}>
                {n} {STATUS_LABEL[k] || k}
              </Tag>
            ))}
          </div>
          {buckets.length ? (
            <Segmented
              size="small"
              style={{ marginBottom: 10 }}
              value={view}
              onChange={setView}
              options={[{ label: "Tracked categories", value: "tracked" }, { label: "Model categories", value: "model" }]}
            />
          ) : null}
          {showBuckets ? (
            <Table size="small" rowKey="bucket" pagination={false} dataSource={buckets} columns={bucketColumns} />
          ) : (
            <Table
              size="small"
              rowKey="category"
              pagination={false}
              dataSource={data.categories || []}
              columns={columns}
            />
          )}
        </>
      ) : null}
    </Modal>
  );
}
